import { KEYS, bindFirst, createSlatePlugin, createTSlatePlugin, getInjectedPlugins, isUrl, pipeInsertDataQuery } from "platejs";
import videoParser from "js-video-url-parser";

//#region src/lib/BaseAudioPlugin.ts
const BaseAudioPlugin = createSlatePlugin({
	key: KEYS.audio,
	node: {
		isElement: true,
		isVoid: true
	}
});

//#endregion
//#region src/lib/BaseFilePlugin.ts
const BaseFilePlugin = createSlatePlugin({
	key: KEYS.file,
	node: {
		isElement: true,
		isVoid: true
	}
});

//#endregion
//#region src/lib/BaseVideoPlugin.ts
const BaseVideoPlugin = createSlatePlugin({
	key: KEYS.video,
	node: {
		dangerouslyAllowAttributes: ["width", "height"],
		isElement: true,
		isVoid: true
	}
});

//#endregion
//#region src/lib/image/transforms/insertImage.ts
const insertImage = (editor, url, options = {}) => {
	const image = {
		children: [{ text: "" }],
		type: editor.getType(KEYS.img),
		url
	};
	editor.tf.insertNodes(image, {
		nextBlock: true,
		...options
	});
};

//#endregion
//#region src/lib/image/transforms/insertImageFromFiles.ts
const insertImageFromFiles = (editor, files, options = {}) => {
	for (const file of files) {
		const reader = new FileReader();
		const [mime] = file.type.split("/");
		if (mime === "image") {
			reader.addEventListener("load", async () => {
				if (!reader.result) return;
				const uploadImage = editor.getOptions(BaseImagePlugin).uploadImage;
				insertImage(editor, uploadImage ? await uploadImage(reader.result) : reader.result, options);
			});
			reader.readAsDataURL(file);
		}
	}
};

//#endregion
//#region src/lib/image/utils/isImageUrl.ts
const imageExtensions = new Set([
	"3dv",
	"ai",
	"amf",
	"art",
	"art",
	"ase",
	"awg",
	"blp",
	"bmp",
	"bw",
	"bw",
	"cd5",
	"cdr",
	"cgm",
	"cit",
	"cmx",
	"cpt",
	"cr2",
	"cur",
	"cut",
	"dds",
	"dib",
	"djvu",
	"dxf",
	"e2d",
	"ecw",
	"egt",
	"egt",
	"emf",
	"eps",
	"exif",
	"fs",
	"gbr",
	"gif",
	"gpl",
	"grf",
	"hdp",
	"icns",
	"ico",
	"iff",
	"iff",
	"int",
	"int",
	"inta",
	"jfif",
	"jng",
	"jp2",
	"jpeg",
	"jpg",
	"jps",
	"jxr",
	"lbm",
	"lbm",
	"liff",
	"max",
	"miff",
	"mng",
	"msp",
	"nitf",
	"nrrd",
	"odg",
	"ota",
	"pam",
	"pbm",
	"pc1",
	"pc2",
	"pc3",
	"pcf",
	"pct",
	"pcx",
	"pcx",
	"pdd",
	"pdn",
	"pgf",
	"pgm",
	"PI1",
	"PI2",
	"PI3",
	"pict",
	"png",
	"pnm",
	"pns",
	"ppm",
	"psb",
	"psd",
	"psp",
	"px",
	"pxm",
	"pxr",
	"qfx",
	"ras",
	"raw",
	"rgb",
	"rgb",
	"rgba",
	"rle",
	"sct",
	"sgi",
	"sgi",
	"sid",
	"stl",
	"sun",
	"svg",
	"sxd",
	"tga",
	"tga",
	"tif",
	"tiff",
	"v2d",
	"vnd",
	"vrml",
	"vtf",
	"wdp",
	"webp",
	"wmf",
	"x3d",
	"xar",
	"xbm",
	"xcf",
	"xpm"
]);
const isImageUrl = (url) => {
	if (!isUrl(url)) return false;
	const ext = new URL(url).pathname.split(".").pop();
	return imageExtensions.has(ext);
};

//#endregion
//#region src/lib/image/withImageEmbed.ts
/** If inserted text is image url, insert image instead. */
const withImageEmbed = ({ editor, getOptions, tf: { insertData } }) => ({ transforms: { insertData(dataTransfer) {
	if (getOptions().disableEmbedInsert) return insertData(dataTransfer);
	const text = dataTransfer.getData("text/plain");
	if (isImageUrl(text)) {
		insertImage(editor, text);
		return;
	}
	insertData(dataTransfer);
} } });

//#endregion
//#region src/lib/image/withImageUpload.ts
/**
* Allows for pasting images from clipboard. Not yet: dragging and dropping
* images, selecting them through a file system dialog.
*/
const withImageUpload = ({ editor, getOptions, plugin, tf: { insertData } }) => ({ transforms: { insertData(dataTransfer) {
	if (getOptions().disableUploadInsert) return insertData(dataTransfer);
	const mimeType = "text/plain";
	const text = dataTransfer.getData(mimeType);
	const { files } = dataTransfer;
	if (!text && files && files.length > 0) {
		if (!pipeInsertDataQuery(editor, getInjectedPlugins(editor, plugin), {
			data: text,
			dataTransfer,
			mimeType
		})) return insertData(dataTransfer);
		insertImageFromFiles(editor, files);
	} else return insertData(dataTransfer);
} } });

//#endregion
//#region src/lib/image/BaseImagePlugin.ts
/** Enables support for images. */
const BaseImagePlugin = createTSlatePlugin({
	key: KEYS.img,
	node: {
		dangerouslyAllowAttributes: [
			"alt",
			"width",
			"height"
		],
		isElement: true,
		isVoid: true
	},
	parsers: { html: { deserializer: {
		rules: [{ validNodeName: "IMG" }],
		parse: ({ element, type }) => ({
			type,
			url: element.getAttribute("src")
		})
	} } }
}).overrideEditor(withImageUpload).overrideEditor(withImageEmbed).extendEditorTransforms(({ editor }) => ({ insert: { imageFromFiles: bindFirst(insertImageFromFiles, editor) } }));

//#endregion
//#region src/lib/media/insertMedia.ts
const insertMedia = async (editor, { getUrl, type = editor.getType(KEYS.img), ...options } = {}) => {
	const url = getUrl ? await getUrl() : window.prompt(`Enter the URL of the ${type === KEYS.img ? KEYS.img : KEYS.mediaEmbed}`);
	if (!url) return;
	if (type === editor.getType(KEYS.img)) insertImage(editor, url, options);
	else insertMediaEmbed(editor, { url }, options);
};

//#endregion
//#region src/lib/media/parseMediaUrl.ts
const allowedProtocols = new Set(["http:", "https:"]);
const parseMediaUrl = (url, { urlParsers }) => {
	const embed = (() => {
		for (const parser of urlParsers) {
			const data = parser(url);
			if (data) return data;
		}
	})();
	if (embed?.url) try {
		const { protocol } = new URL(embed.url);
		if (!allowedProtocols.has(protocol)) return;
	} catch {
		console.warn(`Could not parse URL: ${embed.url}`);
		return;
	}
	return embed;
};

//#endregion
//#region src/lib/media-embed/parseIframeUrl.ts
const regexMatchSrc = /src=".*?"/;
const regexGroupQuotes = /"([^"]*)"/;
const parseIframeUrl = (url) => {
	if (!url.startsWith("http")) {
		const returnString = (regexMatchSrc.exec(url)?.[0])?.match(regexGroupQuotes)?.[1];
		if (returnString) return returnString;
	}
	return url;
};

//#endregion
//#region src/lib/media-embed/BaseMediaEmbedPlugin.ts
/**
* Enables support for embeddable media such as YouTube or Vimeo videos,
* Instagram posts and tweets or Google Maps.
*/
const BaseMediaEmbedPlugin = createTSlatePlugin({
	key: KEYS.mediaEmbed,
	node: {
		isElement: true,
		isVoid: true
	},
	options: { transformUrl: parseIframeUrl },
	parsers: { html: { deserializer: {
		rules: [{ validNodeName: "IFRAME" }],
		parse: ({ element, type }) => {
			const url = element.getAttribute("src");
			if (url) return {
				type,
				url
			};
		}
	} } }
});

//#endregion
//#region src/lib/media-embed/parseTwitterUrl.ts
const twitterRegex = /^https?:\/\/(?:twitter|x)\.com\/(?:#!\/)?(\w+)\/status(es)?\/(\d+)/;
const TWITTER_ID_INDEX = 3;
const parseTwitterUrl = (url) => {
	const match = twitterRegex.exec(url);
	if (match) return {
		id: match[TWITTER_ID_INDEX],
		provider: "twitter",
		url
	};
};

//#endregion
//#region src/lib/media-embed/parseVideoUrl.ts
const YOUTUBE_PREFIX = "https://www.youtube.com/embed/";
const VIMEO_PREFIX = "https://player.vimeo.com/video/";
const DAILYMOTION_PREFIX = "https://www.dailymotion.com/embed/video/";
const YOUKU_PREFIX = "https://player.youku.com/embed/";
const COUB_PREFIX = "https://coub.com/embed/";
const VIDEO_PROVIDERS = [
	"youtube",
	"vimeo",
	"dailymotion",
	"youku",
	"coub"
];
const parseVideoUrl = (url) => {
	if (!isUrl(url)) return;
	const videoData = videoParser.parse(url);
	if (videoData?.provider && videoData.id) {
		const { id, provider } = videoData;
		return {
			id,
			provider,
			url: {
				coub: `${COUB_PREFIX}${id}`,
				dailymotion: `${DAILYMOTION_PREFIX}${id}`,
				vimeo: `${VIMEO_PREFIX}${id}`,
				youku: `${YOUKU_PREFIX}${id}`,
				youtube: `${YOUTUBE_PREFIX}${id}`
			}[provider]
		};
	}
};

//#endregion
//#region src/lib/media-embed/transforms/insertMediaEmbed.ts
const insertMediaEmbed = (editor, { url = "" }, options = {}) => {
	if (!editor.selection) return;
	const selectionParentEntry = editor.api.parent(editor.selection);
	if (!selectionParentEntry) return;
	const [, path] = selectionParentEntry;
	editor.tf.insertNodes({
		children: [{ text: "" }],
		type: editor.getType(KEYS.mediaEmbed),
		url
	}, {
		at: path,
		nextBlock: true,
		...options
	});
};

//#endregion
//#region src/lib/placeholder/transforms/insertPlaceholder.ts
const insertPlaceholder = (editor, mediaType, options) => {
	editor.tf.withoutNormalizing(() => editor.tf.insertNodes({
		children: [{ text: "" }],
		mediaType,
		type: editor.getType(KEYS.placeholder)
	}, options));
};
const insertImagePlaceholder = (editor, options) => insertPlaceholder(editor, KEYS.img, options);
const insertVideoPlaceholder = (editor, options) => insertPlaceholder(editor, KEYS.video, options);
const insertAudioPlaceholder = (editor, options) => insertPlaceholder(editor, KEYS.audio, options);
const insertFilePlaceholder = (editor, options) => insertPlaceholder(editor, KEYS.file, options);

//#endregion
//#region src/lib/placeholder/transforms/setMediaNode.ts
const setMediaNode = (editor, props, options) => editor.tf.setNodes(props, options);

//#endregion
//#region src/lib/placeholder/BasePlaceholderPlugin.ts
const BasePlaceholderPlugin = createTSlatePlugin({
	key: KEYS.placeholder,
	node: {
		isElement: true,
		isVoid: true
	}
}).extendEditorTransforms(({ editor }) => ({ insert: {
	audioPlaceholder: bindFirst(insertAudioPlaceholder, editor),
	filePlaceholder: bindFirst(insertFilePlaceholder, editor),
	imagePlaceholder: bindFirst(insertImagePlaceholder, editor),
	videoPlaceholder: bindFirst(insertVideoPlaceholder, editor)
} }));

//#endregion
export { BaseFilePlugin as C, BaseVideoPlugin as S, withImageUpload as _, insertImagePlaceholder as a, insertImageFromFiles as b, insertMediaEmbed as c, parseTwitterUrl as d, BaseMediaEmbedPlugin as f, BaseImagePlugin as g, insertMedia as h, insertFilePlaceholder as i, VIDEO_PROVIDERS as l, parseMediaUrl as m, setMediaNode as n, insertPlaceholder as o, parseIframeUrl as p, insertAudioPlaceholder as r, insertVideoPlaceholder as s, BasePlaceholderPlugin as t, parseVideoUrl as u, withImageEmbed as v, BaseAudioPlugin as w, insertImage as x, isImageUrl as y };